import { Fragment } from 'react';
import {
  DevHubBox,
  DevHubButton,
  DevHubCard,
  DevHubDynamicIcon,
  devhubMakeStyles,
  type DevHubTheme,
  DevHubTooltip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { Device } from '../types/InsightsTypes';
import LinuxIcon from '../../../app/icons/linux_logo.svg';
import MicrosoftIcon from '../../../app/icons/microsoft_logo.svg';

const useStyles = devhubMakeStyles((theme: DevHubTheme) => ({
  osRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.25rem',
  },
  osImage: {
    height: '2.25rem',
    width: 'auto',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '2px 0',
  },
  detailLabel: {
    fontWeight: 600,
    marginRight: '0.5rem',
    whiteSpace: 'nowrap',
  },
  detailValue: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  viewIopButtonWrapper: {
    display: 'flex',
    justifyContent: 'flex-start',
    '& button': {
      fontWeight: 700,
      transition:
        'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease',
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
      },
    },
  },
}));

interface Props {
  device: Device;
  showAppName?: boolean;
}

/**
 * A component to display an individual device in a list
 * @param props props of the component
 * @param props.device an object to represent a device
 */
function DeviceCard(props: Props) {
  const displayAppName = props?.showAppName ?? false;
  const classes = useStyles();
  const isDeviceIncomplete =
    (props?.device?.tier ?? null) === null ||
    (props?.device?.environment ?? null) === null;

  const getAppName = () => {
    if (displayAppName) {
      if (
        Array.isArray(props?.device?.applicationsPaged?.applications) &&
        props.device.applicationsPaged.applications.length > 0
      ) {
        const appNames = props.device.applicationsPaged.applications.map(
          app => app.name,
        );
        const singularOrPlural =
          appNames.length > 1 ? 'Applications' : 'Application';
        const formattedAppName =
          appNames.length > 1 ? appNames.join(', ') : appNames[0];
        return `${singularOrPlural}: ${formattedAppName}`;
      }
    }
    return '';
  };

  const getOSIcon = (type?: string | null) => {
    const name = type ?? '';
    if (name.toLowerCase().includes('linux')) {
      return <img className={classes.osImage} src={LinuxIcon} alt="Linux" />;
    }
    if (name.toLowerCase().includes('windows')) {
      return (
        <img className={classes.osImage} src={MicrosoftIcon} alt="Windows" />
      );
    }
    return (
      <DevHubDynamicIcon iconName="HelpOutline" className={classes.osImage} />
    );
  };

  const detailFields = [
    { label: 'Tier', value: props?.device?.tier },
    { label: 'Env', value: props?.device?.environment },
    { label: 'IP Address', value: props?.device?.ipAddress },
    { label: 'Serial No.', value: props?.device?.serialNumber },
    { label: 'Status', value: props?.device?.operationalStatus },
  ];

  const secondaryText = (
    <DevHubBox mt={0.5}>
      {detailFields.map(({ label, value }) => (
        <div key={label} className={classes.detailRow}>
          <DevHubTypography variant="body2" className={classes.detailLabel}>
            {label}:
          </DevHubTypography>
          <DevHubTypography variant="body2" className={classes.detailValue}>
            {value ?? '—'}
          </DevHubTypography>
        </div>
      ))}
    </DevHubBox>
  );

  const handleFixMe = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    window.open(
      'https://comcast.service-now.com/iop?id=sc_cat_item&sys_id=b8d9a920db73d85053fb5a58dc961977&sysparm_category=83af56cedba5dc5853fb5a58dc961961',
      '_blank',
      'noopener,noreferrer',
    );
  };

  const handleOpen = () => {
    window.open(props.device.href, '_blank', 'noopener,noreferrer');
  };

  const osRow = (
    <div className={classes.osRow}>
      <DevHubTypography variant="body2">
        {props?.device?.osInfo?.name ?? 'Unknown OS'}
      </DevHubTypography>
      <DevHubTooltip
        title={props?.device?.osInfo?.version ?? 'Unknown'}
        placement="bottom"
      >
        <div>{getOSIcon(props?.device?.osInfo?.name)}</div>
      </DevHubTooltip>
    </div>
  );

  return (
    <DevHubCard
      style={{
        borderRadius: '140px',
        overflow: 'hidden',
      }}
      body={
        <Fragment>
          {osRow}
          {secondaryText}
          {isDeviceIncomplete ? (
            <DevHubTypography variant="body2">
              Click the &apos;Fix Me&apos; button below and assign missing
              tier/environment for this device.
            </DevHubTypography>
          ) : (
            <DevHubBox mt={1} className={classes.viewIopButtonWrapper}>
              <DevHubButton
                variant="outlined"
                color="primary"
                height="medium"
                startIcon={<DevHubDynamicIcon iconName="OpenInNew" />}
                onClick={handleOpen}
                borderRadiusType="allCorners"
                borderRadiusSize="small"
              >
                View on IOP
              </DevHubButton>
            </DevHubBox>
          )}
        </Fragment>
      }
      cardButton={
        isDeviceIncomplete && (
          <DevHubButton
            fullWidth
            onClick={handleFixMe}
            buttonColor="black"
            textColor="white"
          >
            Fix Me
            <DevHubBox ml={0.5} mt={0.5}>
              <DevHubDynamicIcon
                color="orange"
                fontSize="medium"
                iconName="ReportProblem"
              />
            </DevHubBox>
          </DevHubButton>
        )
      }
      cardButtonPosition="workbox"
      density={displayAppName ? 'cozy' : 'compact'}
      openInNewTab
      title={props?.device?.hostname ?? 'Unknown Device'}
      subTitle={getAppName()}
    />
  );
}

export default DeviceCard;
